import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { SummaryType } from 'views/bluviu-nutrition/features/scenarios/types/scenario-result.type';

import { Alert, Box, Button, Grid as MuiGrid, Typography, useTheme } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';
import ButtonGroup from 'components/button-group';
import Error from 'components/error';
import FilterSidebar from 'components/filter-sidebar';

import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';
import { AlertCircle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import BluViuNutritionTabsHeader from 'views/bluviu-nutrition/components/bluviu-nutrition-tabs-header';
import { ScenarioProvider, useScenarioState } from 'views/bluviu-nutrition/features/scenarios/context/scenario-context';
import { useSelectedSite } from 'views/bluviu-nutrition/hooks/use-selected-site';
import { ScenariosParams } from '../../types/scenarios-params.type';

import Paper from 'components/paper';
import ScenarioBottomBar from 'views/bluviu-nutrition/features/scenarios/components/view-results/results/scenario-bottom-bar';
import ScenarioFeedList from 'views/bluviu-nutrition/features/scenarios/components/view-results/results/scenario-feed-list';
import TopPerformers from 'views/bluviu-nutrition/features/scenarios/components/view-results/results/top-performers';
import {
  ScenarioResultProvider,
  useScenarioResultState,
} from 'views/bluviu-nutrition/features/scenarios/context/scenario-result-context';

type Props = {};

const ViewScenarioResult: React.FC<Props> = () => {
  const intl = useIntl();
  const theme = useTheme();
  const params = useParams<ScenariosParams>();

  const { selectedSite } = useSelectedSite();
  const {
    scenarioQuery: { data: scenario },
  } = useScenarioState();

  const {
    scenarioResultQuery: { refetch, isFetching, isError: isGetScenarioSummaryError, error },
    week,
    weight,
    summaries,
    summaryResult,
    showTopPerformers,
    selectedSummaryType,
    setWeek,
    setWeight,
    setShowGraph,
    setSelectedGraphData,
    setSelectedFeedPlans,
    setShowTopPerformers,
    setSelectedSummaryType,
  } = useScenarioResultState();

  const PAGE_NAME_ID = 'bluViuNutrition.viewScenarioResultGt2';

  // STATE
  const [showCheckbox, setShowCheckbox] = useState(false);
  const [showCompareBottomBar, setShowCompareBottomBar] = useState(false);
  const [weekError, setWeekError] = useState<boolean | null>(null);
  const [weightError, setWeightError] = useState<boolean | null>(null);

  const summaryLength = useMemo(() => summaries?.length ?? 0, [summaries]);

  useEffect(() => {
    if (summaryResult?.maxAllowedWeight !== undefined && isNaN(weight)) {
      setWeight(Math.floor(summaryResult?.maxAllowedWeight));
    }
    if (summaryResult?.maxAllowedWeek !== undefined && isNaN(week)) {
      setWeek(summaryResult?.maxAllowedWeek);
    }
  }, [summaryResult]);

  // #region HANDLERS

  const validateWeek = (value: any) => {
    if (!value) {
      return true;
    }
    // Check if the value is within the allowed range
    if (summaryResult?.minAllowedWeek && value < summaryResult?.minAllowedWeek) {
      return true;
    }
    if (summaryResult?.maxAllowedWeek && value > summaryResult?.maxAllowedWeek) {
      return true;
    }
    return null;
  };

  const validateWeight = (value: any) => {
    if (!value) {
      return true;
    }
    // Check if the value is within the allowed range
    if (summaryResult?.minAllowedWeight && value < summaryResult?.minAllowedWeight) {
      return true;
    }
    if (summaryResult?.maxAllowedWeight && value > Number(summaryResult?.maxAllowedWeight)) {
      return true;
    }

    return null;
  };

  const handleGetResults = () => {
    if (selectedSummaryType == SummaryType.Week && (!!weekError || validateWeek(week))) {
      setWeekError(true);
      return;
    } else if (selectedSummaryType == SummaryType.Weight && (!!weightError || validateWeight(weight))) {
      setWeightError(true);
      return;
    }
    refetch();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleGetResults();
    }
  };

  // #endregion
  // #region renders

  const renderSummaryType = () => {
    return Object.entries(SummaryType).map(([key, value]) => (
      <RadioSelectItem
        key={key}
        value={value}
        onClick={() => {
          (setSelectedSummaryType(value), setWeekError(false), setWeightError(false));
        }}>
        <FormattedMessage id={`${PAGE_NAME_ID}.filters.types.${value}`} defaultMessage={value.toUpperCase()} />
      </RadioSelectItem>
    ));
  };

  const renderTabOptions = () => (
    <ButtonGroup
      value={'group'}
      buttons={[
        {
          label: <FormattedMessage id={`${PAGE_NAME_ID}.tabs.result`} />,
          value: 'left',
          sx: {
            '&.MuiToggleButton-root.Mui-selected': {
              backgroundColor: theme.palette.lightBlue.o75,
            },
          },
          state: false,
          handleButtonClick: () => {
            setShowGraph(false);
            setShowCheckbox(false);
            setShowCompareBottomBar(false);
            setShowTopPerformers(true);
            setSelectedFeedPlans([]);
            setSelectedGraphData([]);
          },
        },
        {
          label: <FormattedMessage id={`${PAGE_NAME_ID}.tabs.compare`} />,
          value: 'center',
          sx: {
            '&.MuiToggleButton-root.Mui-selected': {
              backgroundColor: theme.palette.lightBlue.o75,
            },
            display: summaryLength === 1 ? 'none' : 'inline-flex',
          },
          state: true,
          handleButtonClick: () => {
            setShowGraph(false);
            setShowCheckbox((current) => !current);
            setShowTopPerformers(false);
          },
        },
        {
          label: <FormattedMessage id={`${PAGE_NAME_ID}.tabs.graph`} />,
          value: 'right',
          sx: {
            '&.MuiToggleButton-root.Mui-selected': {
              backgroundColor: theme.palette.lightBlue.o75,
            },
            display: summaryLength > 1 ? 'none' : 'inline-flex',
          },
          state: true,
          handleButtonClick: () => {
            setShowGraph(true);
            setShowCheckbox(false);
            setShowCompareBottomBar(false);
            setShowTopPerformers(false);
            setSelectedFeedPlans([]);
            setSelectedGraphData([]);
          },
        },
      ]}
      handleClick={(e) => console.log(e)} // TODO fix this button handler
    />
  );

  const renderTopPerformers = () => (
    <TopPerformers
      showTopPerformers={showTopPerformers}
      renderTabOptions={() => renderTabOptions()}
      selectedSummaryType={selectedSummaryType}
      summaryResult={summaryResult}
    />
  );

  const checkZeroValue =
    (summaryResult?.minAllowedWeight === 0 && summaryResult?.maxAllowedWeight === 0) ||
    (summaryResult?.maxAllowedWeek === 0 && summaryResult?.minAllowedWeek === 0);

  // #endregion

  if (isGetScenarioSummaryError) return <Error error={error} />;
  return (
    <>
      <Typography variant="h3" />
      <BluViuNutritionTabsHeader
        intro={
          <Typography color="white">
            <FormattedMessage id="general.confidential" />
          </Typography>
        }
        utils={
          <Breadcrumbs
            crumbs={[
              {
                text: selectedSite?.name || `${params.globalSiteId}`,
                url: `${params.globalSiteId}`,
              },
              {
                text: scenario?.name || params.scenarioId,
                url: `${params.scenarioId}`,
              },
              {
                text: 'result',
                url: `result`,
              },
            ]}
          />
        }
      />

      {checkZeroValue ? (
        <Paper transparent>
          <Alert severity="warning">
            <Typography variant="body1">
              <FormattedMessage id={`${PAGE_NAME_ID}.productionRangeMessage`} />
            </Typography>
          </Alert>
        </Paper>
      ) : (
        <Box display={'flex'} flexDirection={'column'} gap={2}>
          <FilterSidebar sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box display={'flex'} flexDirection={'row'} gap={4} alignItems={'center'}>
              <Select value={selectedSummaryType} label={intl.formatMessage({ id: `${PAGE_NAME_ID}.title` })}>
                {renderSummaryType()}
              </Select>
              {selectedSummaryType === SummaryType.Week && (
                <TextField
                  type="number"
                  value={week}
                  label={intl.formatMessage(
                    { id: `${PAGE_NAME_ID}.filters.weekLabel` },
                    {
                      minWeek: summaryResult?.minAllowedWeek,
                      maxWeek: summaryResult?.maxAllowedWeek,
                    }
                  )}
                  onFocus={() => {
                    setWeekError(false);
                  }}
                  error={!!weekError}
                  onBlur={() => setWeekError(false)}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setWeek(value);
                    setWeekError(false);
                  }}
                  onKeyDown={handleKeyDown}
                />
              )}
              {selectedSummaryType === SummaryType.Weight && (
                <TextField
                  type="number"
                  value={Math.floor(weight)}
                  endAdornment={`g`}
                  label={intl.formatMessage(
                    { id: `${PAGE_NAME_ID}.filters.weightLabel` },
                    {
                      minWeight: summaryResult?.minAllowedWeight,
                      maxWeight: Math.floor(summaryResult?.maxAllowedWeight ?? 0),
                    }
                  )}
                  error={!!weightError}
                  onBlur={() => setWeightError(false)}
                  onFocus={() => {
                    setWeightError(false);
                  }}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    setWeight(Math.floor(value));
                    setWeightError(false);
                  }}
                  onKeyDown={handleKeyDown}
                />
              )}
              <Button onClick={handleGetResults} disabled={isFetching}>
                <FormattedMessage id={`${PAGE_NAME_ID}.getResult`} />
              </Button>
            </Box>
            {selectedSummaryType === SummaryType.Week && weekError && (
              <Alert icon={false} sx={{ bgcolor: 'background.paper' }}>
                <MuiGrid container alignItems="center">
                  <AlertCircle color={theme.palette.darkBlue.main} />
                  <Typography variant="caption" ml={4}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.adjustWeekLabel`} />
                  </Typography>
                </MuiGrid>
                <Typography variant="body1" sx={{ color: '#012231', mt: 4 }}>
                  <FormattedMessage
                    id={intl.formatMessage(
                      { id: `${PAGE_NAME_ID}.weeksRangeRequired` },
                      {
                        minWeek: summaryResult?.minAllowedWeek,
                        maxWeek: summaryResult?.maxAllowedWeek,
                      }
                    )}
                  />
                </Typography>
              </Alert>
            )}
            {selectedSummaryType === SummaryType.Weight && weightError && (
              <Alert icon={false} sx={{ bgcolor: 'background.paper' }}>
                <MuiGrid container alignItems="center">
                  <AlertCircle color={theme.palette.darkBlue.main} />
                  <Typography variant="caption" ml={4}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.adjustWeightLabel`} />
                  </Typography>
                </MuiGrid>
                <Typography variant="body1" sx={{ color: '#012231', mt: 4 }}>
                  <FormattedMessage
                    id={intl.formatMessage(
                      { id: `${PAGE_NAME_ID}.weightsRangeRequired` },
                      {
                        minWeight: Math.floor(summaryResult?.minAllowedWeight ?? 0),
                        maxWeight: Math.floor(summaryResult?.maxAllowedWeight ?? 0),
                      }
                    )}
                  />
                </Typography>
              </Alert>
            )}
          </FilterSidebar>

          <ScenarioFeedList
            renderTopPerformers={() => renderTopPerformers()}
            showCheckbox={showCheckbox}
            summaryResult={summaryResult}
            setShowCompareBottomBar={setShowCompareBottomBar}
          />
        </Box>
      )}
      <ScenarioBottomBar
        showCompareBottomBar={showCompareBottomBar}
        clearSelection={() => {
          setSelectedFeedPlans([]);
          setSelectedGraphData([]);
          setShowCompareBottomBar(false);
        }}
      />
    </>
  );
};

const ScenarioResultContainer = () => {
  return (
    <ScenarioResultProvider>
      <ScenarioProvider>
        <ViewScenarioResult />
      </ScenarioProvider>
    </ScenarioResultProvider>
  );
};

export default React.memo(ScenarioResultContainer);
